import { ArrowDropUp, ArrowDropDown, Lock as LockIcon, LockOpen as LockOpenIcon } from '@mui/icons-material'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import React, { useEffect, useCallback, useState } from 'react'
import { Controller } from 'react-hook-form'

import { HHFormDesktopDatePicker, HHFormAutocompleteField, HHFormNumberField, HHFormPriceField } from 'components/form-fields/v5'
import ActionsAutocompleteFormField from 'components/pricing/Services/edit-service/ActionsAutocompleteFormField'
import TagsSelect from 'components/tags/TagsSelect'
import { MAX_CONTAINER_QUANTITY } from 'settings/constants/service'
import T from 'T'
import { isAddressObjectEmpty } from 'utils/helper'

import { useCreateDraftConfiguredServiceContext } from '../CreateDraftConfiguredServiceContext'

const isWithinContainerLimit = ({ floatValue }) => floatValue === undefined || floatValue <= MAX_CONTAINER_QUANTITY

const QuantityFieldWithControls = React.memo(({ control, errors, handleIncrementQuantity, handleDecrementQuantity }) => {
  return (
    <HHFormNumberField
      fullWidth
      showErrorMessage
      control={control}
      deprecatedLabel={false}
      error={errors.quantity?.message}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <Stack
              direction="column"
              sx={{
                height: '50%',
              }}
            >
              <IconButton
                aria-label={T.INCREMENT_QUANTITY}
                size="small"
                sx={{
                  height: '24px',
                  width: '24px',
                }}
                onClick={handleIncrementQuantity}
              >
                <ArrowDropUp />
              </IconButton>
              <IconButton
                aria-label={T.DECREMENT_QUANTITY}
                size="small"
                sx={{
                  height: '24px',
                  width: '24px',
                }}
                onClick={handleDecrementQuantity}
              >
                <ArrowDropDown />
              </IconButton>
            </Stack>
          </InputAdornment>
        ),
      }}
      isAllowed={isWithinContainerLimit}
      label={T.QUANTITY}
      name="quantity"
      rules={{
        required: T.REQUIRED_HELPER_TEXT,
        min: { value: 1, message: T.QUANTITY_MUST_BE_POSITIVE },
      }}
      sx={{ '& input': { textAlign: 'center' } }}
    />
  )
})

const AutoCalculateToggleButton = React.memo(({ isAutoCalculate, onToggle, canEditPrice }) => {
  const isDisabled = !canEditPrice
  const tooltipTitle = isDisabled ? T.NO_PERMISSION_TO_SET_CUSTOM_PRICE : T.CALCULATE_SERVICE_PRICE_FROM_CONTAINER_COUNT

  return (
    <Tooltip placement="top" title={tooltipTitle}>
      <span style={{ cursor: isDisabled ? 'not-allowed' : 'default', display: 'inline-flex' }}>
        <IconButton
          aria-label={T.TOGGLE_AUTOMATIC_PRICE_CALCULATION}
          disabled={isDisabled}
          sx={{
            color: isAutoCalculate ? 'primary.main' : 'action.active',
            '&.Mui-disabled': {
              color: 'primary.main',
              opacity: 0.7,
              pointerEvents: 'none',
            },
          }}
          onClick={isDisabled ? undefined : onToggle}
        >
          {isAutoCalculate ? <LockIcon /> : <LockOpenIcon />}
        </IconButton>
      </span>
    </Tooltip>
  )
})

const PriceFieldWithAutoCalculate = React.memo(({ control, canEditPrice, isAutoCalculate }) => {
  return (
    <HHFormPriceField
      fullWidth
      control={control}
      deprecatedLabel={false}
      disabled={!canEditPrice || isAutoCalculate}
      label={T.PRICE}
      name="price"
    />
  )
})

const DraftServiceFooter = () => {
  const {
    control,
    errors,
    locations,
    actionList,
    slotPropsPopper,
    getLocationOptionLabel,
    handleLocationAutocompleteChange,
    isSelectedPricedServiceOnRequest,
    getValues,
    setValue,
    watch,
    selectedPricedService,
    canEditPrice,
    serviceTags,
    renderAsPanel,
  } = useCreateDraftConfiguredServiceContext()

  const autoCalculatePrice = watch('autoCalculatePrice')
  const quantity = watch('quantity')
  const formLocation = watch('location')
  const locationHasNoAddress = isAddressObjectEmpty(formLocation?.address)

  // If user lacks edit-price permission, always enforce auto-calculate
  const [isAutoCalculate, setIsAutoCalculate] = useState(canEditPrice ? autoCalculatePrice ?? true : true)

  const handleToggleAutoCalculate = useCallback(() => {
    if (!canEditPrice) {
      return
    }

    const newValue = !isAutoCalculate
    setIsAutoCalculate(newValue)
    setValue('autoCalculatePrice', newValue, { shouldValidate: true, shouldDirty: true })

    if (newValue && selectedPricedService) {
      const quantityValue = quantity ? Number(quantity) : 0
      if (quantityValue > 0) {
        const basePrice = selectedPricedService.value || 0
        const calculatedPrice = basePrice * quantityValue
        setValue('price', calculatedPrice, { shouldValidate: true, shouldDirty: true })
      }
    }
  }, [isAutoCalculate, selectedPricedService, quantity, setValue, canEditPrice])

  const handleIncrementQuantity = useCallback(() => {
    const currentValue = getValues('quantity')
    const newValue = Math.min(MAX_CONTAINER_QUANTITY, Math.max(1, Number(currentValue || 0) + 1))
    setValue('quantity', newValue, { shouldValidate: true, shouldDirty: true })
  }, [getValues, setValue])

  const handleDecrementQuantity = useCallback(() => {
    const currentValue = getValues('quantity')
    const newValue = Math.max(1, (currentValue || 0) - 1)
    setValue('quantity', newValue, { shouldValidate: true, shouldDirty: true })
  }, [getValues, setValue])

  useEffect(() => {
    if (!canEditPrice) {
      setIsAutoCalculate(true)
      setValue('autoCalculatePrice', true, { shouldValidate: false })
    } else {
      setIsAutoCalculate(autoCalculatePrice ?? true)
    }
  }, [autoCalculatePrice, canEditPrice, setValue])

  useEffect(() => {
    if (isAutoCalculate && selectedPricedService) {
      const quantityValue = quantity ? Number(quantity) : 0
      if (quantityValue > 0) {
        const basePrice = selectedPricedService.value || 0
        const calculatedPrice = basePrice * quantityValue
        setValue('price', calculatedPrice, { shouldValidate: true, shouldDirty: true })
      }
    }
  }, [isAutoCalculate, quantity, selectedPricedService, setValue])

  return (
    <Grid
      container
      alignItems="flex-start"
      columnSpacing={2}
      flexWrap="wrap"
      rowSpacing={2}
      sx={{
        px: { xs: 2, sm: renderAsPanel ? 4 : 10 },
        py: 3,
        bgcolor: 'background.default',
        borderTop: 1,
        borderTopStyle: 'solid',
        borderTopColor: 'divider',
      }}
    >
      {/* Service Address */}
      <Grid size="grow" sx={{ minWidth: 140 }}>
        <HHFormAutocompleteField
          fullWidth
          control={control}
          deprecatedLabel={false}
          error={!!errors.location || locationHasNoAddress}
          getOptionLabel={getLocationOptionLabel}
          helperText={errors.location?.message || (locationHasNoAddress ? T.LOCATION_NO_ADDRESS_WARNING : '')}
          label={T.SERVICE_ADDRESS}
          name="location"
          options={locations}
          rules={{ required: T.REQUIRED_HELPER_TEXT }}
          slotProps={slotPropsPopper}
          onChangeMiddleware={handleLocationAutocompleteChange}
        />
      </Grid>

      {/* Service Tags */}
      <Grid size="grow" sx={{ minWidth: 140 }}>
        <Controller
          control={control}
          name="tags"
          // eslint-disable-next-line react/jsx-no-bind -- RHF Controller's render-prop API; not hoistable
          render={({ field: { value, onChange } }) => (
            <TagsSelect
              deprecatedLabel={false}
              label={`${T.SERVICE} ${T.TAGS}`}
              selectedTags={value}
              slotProps={slotPropsPopper}
              sx={{ bgcolor: 'background.paper' }}
              tags={serviceTags}
              // eslint-disable-next-line react/jsx-no-bind -- adapts TagsSelect's (event, value) signature to RHF's onChange
              onChange={(e, val) => onChange(val)}
            />
          )}
        />
      </Grid>

      {/* Service Start Date */}
      <Grid size="auto" sx={{ width: 160 }}>
        <HHFormDesktopDatePicker
          clearable
          fullWidth
          control={control}
          label={isSelectedPricedServiceOnRequest ? T.FIRST_WORK_ORDER_DATE : T.SERVICE_START_DATE}
          name="serviceStartDate"
          rules={{
            validate: value => {
              if (!isSelectedPricedServiceOnRequest) return true
              const { forPublish, onRequestAction } = getValues()
              if (!forPublish) return true
              const hasAction = !!onRequestAction
              const hasDate = !!value
              if (hasAction && !hasDate) return T.WO_DATE_REQUIRED_IF_ACTION_SET
              return true
            },
          }}
          slotProps={slotPropsPopper}
          TextFieldProps={{
            clearable: true,
            deprecatedLabel: false,
            helperText: errors?.serviceStartDate?.message || T.THIS_CAN_BE_SET_UP_LATER_MSG,
            error: !!errors.serviceStartDate,
          }}
        />
      </Grid>

      {/* On-request Action */}
      {isSelectedPricedServiceOnRequest && (
        <Grid size="auto" sx={{ width: 160 }}>
          <ActionsAutocompleteFormField
            control={control}
            name="onRequestAction"
            options={actionList}
            rules={{
              validate: value => {
                if (!isSelectedPricedServiceOnRequest) return true
                const { forPublish, serviceStartDate } = getValues()
                if (!forPublish) return true
                const hasDate = !!serviceStartDate
                const hasAction = !!value
                if (hasDate && !hasAction) return T.ACTION_REQUIRED_IF_WO_DATE_SET
                return true
              },
            }}
            slotProps={slotPropsPopper}
          />
        </Grid>
      )}

      {/* Quantity + Auto-calculate toggle + Price */}
      {selectedPricedService && (
        <Grid container alignItems="flex-end" columnSpacing={1} size="auto" sx={{ mr: 6 }}>
          <Grid sx={{ width: 90 }}>
            <QuantityFieldWithControls
              control={control}
              errors={errors}
              handleDecrementQuantity={handleDecrementQuantity}
              handleIncrementQuantity={handleIncrementQuantity}
            />
          </Grid>
          <Grid sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end' }}>
            <AutoCalculateToggleButton canEditPrice={canEditPrice} isAutoCalculate={isAutoCalculate} onToggle={handleToggleAutoCalculate} />
          </Grid>
          <Grid sx={{ width: 90 }}>
            <PriceFieldWithAutoCalculate canEditPrice={canEditPrice} control={control} isAutoCalculate={isAutoCalculate} />
          </Grid>
        </Grid>
      )}
    </Grid>
  )
}

export default React.memo(DraftServiceFooter)
